/**
 * Scroll tracking feature
 * Tracks scroll position and calls callbacks
 */

import type Lenis from 'lenis'
import type { ScrollTrackingCallbacks } from './types'

export interface ScrollTrackingInstance {
  /** Start tracking scroll position */
  start: () => void
  /** Stop tracking scroll position */
  stop: () => void
  /** Check if currently tracking */
  isTracking: () => boolean
  /** Set flag to prevent saving during restoration */
  setRestoring: (isRestoring: boolean) => void
}

/**
 * Setup scroll tracking
 * @param lenisInstance - Lenis scroll instance (optional)
 * @param callbacks - Callback functions
 * @returns Scroll tracking instance
 */
export function setupScrollTracking(
  lenisInstance: Lenis | null,
  callbacks: ScrollTrackingCallbacks = {},
): ScrollTrackingInstance {
  let isTracking = false
  let isRestoring = false
  let scrollHandler: (() => void) | null = null
  let lenisScrollHandler: ((e: any) => void) | null = null

  const start = () => {
    if (!isTracking) {
      // Use Lenis scroll event if available, otherwise fallback to window scroll
      if (lenisInstance) {
        lenisScrollHandler = (e: any) => {
          // Don't save scroll position during restoration
          if (!isRestoring && callbacks.onScroll) {
            const scrollY = e?.scroll ?? lenisInstance.scroll ?? window.scrollY
            callbacks.onScroll(scrollY)
          }
        }
        lenisInstance.on('scroll', lenisScrollHandler)
      } else {
        scrollHandler = () => {
          // Don't save scroll position during restoration
          if (!isRestoring && callbacks.onScroll) {
            callbacks.onScroll(window.scrollY)
          }
        }
        window.addEventListener('scroll', scrollHandler, { passive: true })
      }
      isTracking = true
    }
  }

  const stop = () => {
    if (isTracking) {
      if (lenisInstance && lenisScrollHandler) {
        lenisInstance.off('scroll', lenisScrollHandler)
        lenisScrollHandler = null
      } else if (scrollHandler) {
        window.removeEventListener('scroll', scrollHandler)
        scrollHandler = null
      }
      isTracking = false
    }
  }

  const setRestoring = (restoring: boolean) => {
    isRestoring = restoring
  }

  return {
    start,
    stop,
    isTracking: () => isTracking,
    setRestoring,
  }
}
